// Status machines [03 §18]: each transition is one action endpoint in one transaction that
// re-checks permission, SoD (≠ preparer where marked) and approval limit; *(d)* transitions are
// derived by the system and never accepted from a user (BR-DM-013). No `PATCH status`.
import type { PermissionKey } from '../auth/permissions.ts';
import type { GateId } from '../gates/registry.ts';
import { AppError } from '../kernel/errors.ts';
import { defineEnum } from './define.ts';
import type { EnumDef } from './define.ts';

export type TransitionActor = 'user' | 'system';

export interface Transition<S extends string = string> {
  readonly from: readonly S[];
  readonly to: S;
  /** Action endpoint name (`POST /x/{id}/{action}`) or the system event for derived moves. */
  readonly action: string;
  /** Required permission; null for system-derived transitions. */
  readonly permission: PermissionKey | null;
  readonly by: TransitionActor;
  /** Actor must differ from the document's preparer/submitter (action-time SoD). */
  readonly sod?: boolean;
  readonly gates?: readonly GateId[];
  /** Mandatory reason text. */
  readonly reason?: boolean;
  /** Passes only via `<module>.override` (recorded in core.override_event). */
  readonly overrideOnly?: boolean;
  readonly stepUp?: boolean;
  /** Over an approval limit the `approve` permission + limit check applies. */
  readonly approvalLimit?: boolean;
  /** User transition whose permission is the subject document's (e.g. approval_request). */
  readonly permissionFromSubject?: boolean;
}

export interface Machine<S extends string = string> {
  readonly name: string;
  readonly states: EnumDef<S>;
  readonly initial: readonly S[];
  readonly transitions: readonly Transition<S>[];
}

export function defineMachine<const S extends string>(
  name: string,
  states: readonly [S, ...S[]],
  initial: readonly NoInfer<S>[],
  transitions: readonly NoInfer<Transition<S>>[],
): Machine<S> {
  const def = defineEnum(name, states);
  const bad = (s: string) => !def.is(s);
  if (initial.length === 0 || initial.some(bad)) throw new Error(`${name}: bad initial state`);
  for (const t of transitions) {
    if (t.from.length === 0 || t.from.some(bad) || bad(t.to))
      throw new Error(`${name}: transition ${t.action} references an unknown state`);
    const needsKey = t.by === 'user' && !t.permissionFromSubject;
    if (needsKey !== (t.permission !== null))
      throw new Error(
        `${name}: ${t.action} — user transitions need a permission, system ones none`,
      );
  }
  return Object.freeze({
    name,
    states: def,
    initial: Object.freeze([...initial]),
    transitions: Object.freeze(
      transitions.map((t) => Object.freeze({ ...t, from: Object.freeze([...t.from]) })),
    ),
  });
}

/** User transition helper. */
export function userStep<S extends string>(
  from: S | readonly S[],
  to: S,
  action: string,
  permission: PermissionKey | null,
  extra: Omit<Partial<Transition<S>>, 'from' | 'to' | 'action' | 'permission' | 'by'> = {},
): Transition<S> {
  return {
    from: typeof from === 'string' ? [from] : [...from],
    to,
    action,
    permission,
    by: 'user',
    ...extra,
  };
}

/** System-derived transition helper (*(d)*, jobs, agents, webhooks, sync). */
export function systemStep<S extends string>(
  from: S | readonly S[],
  to: S,
  action: string,
): Transition<S> {
  return {
    from: typeof from === 'string' ? [from] : [...from],
    to,
    action,
    permission: null,
    by: 'system',
  };
}

/** All states except the listed ones (for "any→x" rows). */
export function allExcept<const S extends string>(states: readonly S[], ...except: S[]): S[] {
  return states.filter((s) => !except.includes(s));
}

export function transitionsFrom<S extends string>(m: Machine<S>, from: S): Transition<S>[] {
  return m.transitions.filter((t) => t.from.includes(from));
}

export function canTransition<S extends string>(m: Machine<S>, from: S, to: S): boolean {
  return m.transitions.some((t) => t.to === to && t.from.includes(from));
}

/** States with no outgoing transition. */
export function terminalStates<S extends string>(m: Machine<S>): S[] {
  return m.states.values.filter((s) => transitionsFrom(m, s).length === 0);
}

/**
 * Resolves the transition an action endpoint performs from the current state. Unknown action or
 * wrong state → 409 STATE_CONFLICT; a derived transition requested by a user → 422
 * STATUS_NOT_PATCHABLE (derived statuses have no write path).
 */
export function resolveTransition<S extends string>(
  m: Machine<S>,
  from: S,
  action: string,
  actor: TransitionActor,
  to?: S,
): Transition<S> {
  const named = m.transitions.filter(
    (t) => t.action === action && (to === undefined || t.to === to),
  );
  if (actor === 'user' && named.length > 0 && named.every((t) => t.by === 'system')) {
    throw new AppError('STATUS_NOT_PATCHABLE', { params: { machine: m.name, action } });
  }
  // Jobs may run user actions (same checks apply, BR-PERM-002); users never run derived ones.
  const hit = named.find((t) => t.from.includes(from) && (t.by === 'user' || actor === 'system'));
  if (!hit) throw new AppError('STATE_CONFLICT', { params: { machine: m.name, from, action } });
  return hit;
}
